import React, { useMemo } from 'react';

import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import { AsyncPaginate } from 'react-select-async-paginate';

import { useFormControlContext, useTheme } from '@chakra-ui/react';

import { ClearIndicator } from './ClearIndicator';
import { DropdownIndicator } from './DropdownIndicator';
import { MultiValueRemove } from './MultiValueRemove';

const getBorderStyles = (state, colors) => {
  if (state.isFocused) {
    return colors.blackAlpha[200];
  } else if (state.selectProps.isInvalid) {
    return colors.red[500];
  }

  return colors.blackAlpha[100];
};

const getBackgroundColor = (state, colors) => {
  if (state.isDisabled || state.selectProps.isReadOnly) {
    return colors.gray[50];
  }
  if (state.selectProps.variant === 'solid') {
    return colors.primary[600];
  }
  return colors.gray[0];
};

const getIndicatorColor = (state, colors) => {
  if (state.isDisabled) {
    return colors.gray[300];
  }
  if (state.selectProps.variant === 'solid') {
    return colors.gray[0];
  }
  return colors.gray[900];
};

const getIndicatorSeparator = (state, colors) => {
  if (state.isDisabled) {
    return 'transparent';
  }
  if (state.selectProps.variant === 'solid') {
    return colors.gray[0];
  }
  return colors.gray[300];
};

function useDropDownStyles() {
  const theme = useTheme();
  const {
    colors,
    space,
    transition,
    radii,
    fontSizes,
    fontWeights,
    lineHeights,
  } = theme;
  const styles = useMemo(() => {
    // Size-specific dimensions for consistent sizing
    const sizeStyles = {
      sm: {
        height: 'auto',
        minHeight: '32px',
        controlPadding: `${space[0]} ${space[1]} ${space[0]} ${space[3]}`,
        fontSize: fontSizes[7],
        lineHeight: lineHeights[1],
        indicatorHeight: '32px',
        optionFontSize: fontSizes[6],
        multiValueFontSize: fontSizes[7],
        multiValueLineHeight: lineHeights[2],
        multiValuePadding: `${space[0]} ${space[1]} ${space[0]} ${space[1]}`,
        menuFontSize: fontSizes[7],
        optionPadding: `${space['mg1']} ${space[2]}`,
      },
      md: {
        height: 'auto',
        minHeight: '38px',
        controlPadding: `${space[2]} ${space[1]} ${space[2]} ${space[3]}`,
        fontSize: fontSizes[6],
        lineHeight: lineHeights[5],
        indicatorHeight: '38px',
        optionFontSize: fontSizes[5],
        multiValueFontSize: fontSizes[7],
        multiValueLineHeight: lineHeights[7],
        multiValuePadding: `${space['mg0']} ${space['mg0']} ${space['mg0']} ${space['mg1']}`,
        menuFontSize: fontSizes[6],
        optionPadding: `${space['mg2']} ${space[3]}`,
      },
      lg: {
        height: 'auto',
        minHeight: '44px',
        controlPadding: `${space[3]} ${space[1]} ${space[3]} ${space[3]}`,
        fontSize: fontSizes[5],
        lineHeight: lineHeights[5],
        indicatorHeight: '44px',
        optionFontSize: fontSizes[4],
        multiValueFontSize: fontSizes[6],
        multiValueLineHeight: lineHeights[6],
        multiValuePadding: `${space['mg0']} ${space['mg0']} ${space['mg0']} ${space['mg2']}`,
        menuFontSize: fontSizes[5],
        optionPadding: `${space['mg3']} ${space[4]}`,
      },
    };

    const getSizeStyle = (state, property) => {
      const size = state.selectProps?.size || 'md';
      return sizeStyles[size]?.[property] || sizeStyles.md[property];
    };

    return {
      control: (provided, state) => ({
        ...provided,
        cursor: 'pointer',
        padding: 0,
        marginTop: '0px',
        borderColor: getBorderStyles(state, colors),
        boxShadow: 'none',
        backgroundColor: getBackgroundColor(state, colors),
        transitionDuration: transition.duration.normal,
        transitionProperty:
          'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
        ':hover': {
          borderColor: colors.gray[300],
        },
        ':active': {
          borderColor: colors.purple[300],
        },
        ':focus': {
          borderColor: colors.purple[300],
        },
        height: getSizeStyle(state, 'height'),
        minHeight: getSizeStyle(state, 'minHeight'),
        borderRadius:
          state.selectProps.style === 'menu' ? radii['sm'] : radii['base'],
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        padding: state.selectProps.isMulti
          ? state.hasValue
            ? `${space[2]} ${space[1]} ${space[1]} ${space[3]}`
            : `${space[2]} ${space[1]} ${space[2]} ${space[3]}`
          : state.selectProps.size === 'sm'
            ? `${space[0]} ${space[1]} ${space[0]} ${space[3]}`
            : `${space[2]} ${space[1]} ${space[2]} ${space[3]}`,
        minHeight: state.selectProps?.size === 'sm' ? '26px' : '38px',
        height: state.selectProps.isMulti
          ? 'auto'
          : state.selectProps?.size === 'sm'
            ? '26px'
            : '38px',
        flexWrap: 'wrap',
        gap: space[1],
        alignItems: state.selectProps.isMulti ? 'flex-start' : 'center',
      }),
      input: (_, state) => ({
        visibility: 'visible',
        fontSize: getSizeStyle(state, 'fontSize'),
        lineHeight: getSizeStyle(state, 'lineHeight'),
        fontWeight: fontWeights[5],
        color:
          state.selectProps.variant === 'solid'
            ? colors.gray[0]
            : colors.gray[900],
        margin: `0 ${space['mg0']} 0 0`,
      }),
      indicatorsContainer: (provided, state) => ({
        ...provided,
        maxHeight: getSizeStyle(state, 'indicatorHeight'),
        padding: `0 ${space[2]} 0 ${space[1]}`,
      }),
      indicatorSeparator: (provided, state) => ({
        // state doesn't behave how we want it to without passing provided
        width: '1px',
        height: '16px',
        backgroundColor: getIndicatorSeparator(state, colors),
        visibility: state.selectProps.style === 'menu' && 'hidden',
      }),
      clearIndicator: (provided) => ({
        ...provided,
        color: colors.gray[300],
        ':hover': {
          color: colors.gray[300],
        },
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        color: getIndicatorColor(state, colors),
        ':hover': {
          color:
            state.selectProps.variant === 'solid' ? colors.grey[0] : 'initial',
        },
      }),
      placeholder: (provided, state) => ({
        ...provided,
        position: 'absolute',
        fontSize:
          state.selectProps?.size === 'sm' ? fontSizes[7] : fontSizes[6],
        lineHeight:
          state.selectProps?.size === 'sm' ? lineHeights[1] : lineHeights[5],
        fontWeight: fontWeights[1],
        color:
          state.selectProps.variant === 'solid'
            ? colors.grey[0]
            : colors.grey[400],
        marginLeft: '0',
        display: 'flex',
        alignItems: 'center',
        ...(state.selectProps.isMulti && state.hasValue
          ? {
              // For multi-select with values, align with first row
              top: '0',
              transform: 'translateY(-10%)',
            }
          : {
              // For single-select or empty multi-select, center vertically
              top: '50%',
              transform: 'translateY(-50%)',
            }),
      }),
      singleValue: (provided, state) => ({
        ...provided,
        position: 'absolute',
        fontSize:
          state.selectProps?.size === 'sm' ? fontSizes[7] : fontSizes[6],
        lineHeight:
          state.selectProps?.size === 'sm' ? lineHeights[1] : lineHeights[5],
        fontWeight: fontWeights[5],
        color: colors.grey[900],
        display: 'flex',
        alignItems: 'center',
        top: '50%',
        transform: 'translateY(-50%)',
      }),
      multiValue: (provided) => ({
        ...provided,
        margin: `0 ${space[1]} ${space[1]} 0`,
        maxWidth: '100%',
        backgroundColor: colors.grey[50],
        boxShadow: `inset 0 0 0 1px ${colors.grey[300]}`,
        borderRadius: radii['base'],
        fontSize: '14px',
        lineHeight: '1.2',
        fontWeight: fontWeights[7],
        color: colors.grey[900],
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
      }),
      multiValueLabel: (provided, state) => ({
        ...provided,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        padding: getSizeStyle(state, 'multiValuePadding'),
        margin: getSizeStyle(state, 'multiValueMargin'),
        fontSize: getSizeStyle(state, 'multiValueFontSize'),
        lineHeight: getSizeStyle(state, 'multiValueLineHeight'),
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        padding: `${space['mg0']} ${space['mg1']} ${space['mg0']} ${space['mg0']}`,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        ':hover': {
          backgroundColor: colors.red[100],
          boxShadow: `inset 0 0 0 1px ${colors.red[300]}`,
          borderRadius: radii['base'],
          borderTopLeftRadius: '0',
          borderBottomLeftRadius: '0',
          color: colors.red[700],
          cursor: 'pointer',
        },
      }),
      menu: (provided, state) => ({
        ...provided,
        backgroundColor: colors.grey[0],
        cursor: 'pointer',
        marginTop: space[1],
        marginBottom: '0',
        boxShadow: 'none',
        fontSize: getSizeStyle(state, 'menuFontSize'),
        width: state.selectProps.fitContent ? 'max-content' : '100%',
        minWidth: '100%',
      }),
      menuList: (provided, state) => ({
        ...provided,
        padding: '0',
        fontSize: getSizeStyle(state, 'menuFontSize'),
        border: `1px solid ${colors.grey[300]}`,
        boxShadow: '0px 12px 24px 0px var(--chakra-colors-shadow)',
        borderRadius: radii['base'],
        '& > :first-of-type': {
          borderTopRightRadius: '4px',
          borderTopLeftRadius: '4px',
        },
        '& > :last-child': {
          borderBottomRightRadius: '4px',
          borderBottomLeftRadius: '4px',
        },
        height: 'auto',
      }),
      option: (provided, state) => ({
        ...provided,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        padding: getSizeStyle(state, 'optionPadding'),
        color: colors.grey[900],
        opacity: state.isDisabled ? 0.5 : 1,
        backgroundColor: state.isFocused ? 'transparent' : 'transparent',
        ':focus': {
          borderColor: colors.purple[300],
        },
        ':active': {
          borderColor: colors.purple[300],
        },
        ...(!state.isDisabled && {
          ':hover': {
            color: colors.grey[900],
            backgroundColor: colors.grey[50],
          },
        }),
        fontSize: getSizeStyle(state, 'optionFontSize'),
      }),
    };
  }, [theme]);
  return styles;
}

export function Dropdown({ ...props }) {
  const field = useFormControlContext();
  const customStyles = useDropDownStyles();

  let fieldProps = {};
  if (field) {
    fieldProps.isDisabled = field.isReadOnly;
    fieldProps.isRequired = field.isRequired;
    fieldProps.isInvalid = field.isInvalid;
  }

  return (
    <Select
      {...props}
      {...fieldProps}
      isDisabled={props.isDisabled}
      components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
      styles={customStyles}
    />
  );
}

export function CreatableDropdown({ ...props }) {
  const field = useFormControlContext();
  const customStyles = useDropDownStyles();

  let fieldProps = {};
  if (field) {
    fieldProps.isDisabled = field.isReadOnly;
    fieldProps.isRequired = field.isRequired;
    fieldProps.isInvalid = field.isInvalid;
  }

  return (
    <CreatableSelect
      {...props}
      {...fieldProps}
      components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
      styles={customStyles}
    />
  );
}

export function AsyncDropdown({ ...props }) {
  const field = useFormControlContext();
  const customStyles = useDropDownStyles();

  let fieldProps = {};
  if (field) {
    fieldProps.isDisabled = field.isReadOnly;
    fieldProps.isRequired = field.isRequired;
    fieldProps.isInvalid = field.isInvalid;
  }

  return (
    <AsyncSelect
      {...props}
      {...fieldProps}
      components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
      styles={customStyles}
    />
  );
}

export function AsyncPaginateDropdown({ ...props }) {
  const field = useFormControlContext();
  const customStyles = useDropDownStyles();

  let fieldProps = {};
  if (field) {
    fieldProps.isDisabled = field.isReadOnly;
    fieldProps.isRequired = field.isRequired;
    fieldProps.isInvalid = field.isInvalid;
  }

  return (
    <AsyncPaginate
      {...props}
      {...fieldProps}
      components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
      styles={customStyles}
      key={props.refetchKey}
    />
  );
}

export function AsyncCreatableDropdown({ ...props }) {
  const field = useFormControlContext();
  const customStyles = useDropDownStyles();

  let fieldProps = {};
  if (field) {
    fieldProps.isDisabled = field.isReadOnly;
    fieldProps.isRequired = field.isRequired;
    fieldProps.isInvalid = field.isInvalid;
  }

  return (
    <AsyncCreatableSelect
      {...props}
      {...fieldProps}
      components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
      styles={customStyles}
    />
  );
}
